import React, { useState, useCallback, useEffect } from 'react';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../services/firebase';
import { signUp, updateUserProfile, signInWithGoogle, getGoogleRedirectResult } from '../../services/auth';
import { useAuth } from '../../contexts';
import {
  ProgressBar,
  StepWelcome,
  StepAccount,
  StepTravelStyle,
  StepTravelers,
  StepConfirm,
  WelcomeScreen,
} from './components/onboarding';

type Step = 'welcome' | 'account' | 'travel-style' | 'travelers' | 'confirm' | 'done';

const STEP_NUMBERS: Record<Step, number> = {
  welcome: 1,
  account: 2,
  'travel-style': 3,
  travelers: 4,
  confirm: 5,
  done: 5,
};

const TOTAL_STEPS = 5;

export const OnboardingPage: React.FC = () => {
  const { refreshUser } = useAuth();

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  // Form data
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');

  // Preferences
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedCompanions, setSelectedCompanions] = useState<string[]>([]);
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);

  // UI state
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  // Google sign-in flag - if true, we skip account step
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [googleUid, setGoogleUid] = useState<string | null>(null);

  // Check for Google redirect result on mount
  useEffect(() => {
    const checkRedirect = async () => {
      try {
        const result = await getGoogleRedirectResult();
        if (result?.user) {
          const user = result.user;
          const names = user.displayName?.split(' ') || [''];
          setFirstName(names[0] || '');
          setLastName(names.slice(1).join(' ') || '');
          setEmail(user.email || '');
          setIsGoogleUser(true);
          setGoogleUid(user.uid);

          // Create Firestore doc if new user
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (!docSnap.exists()) {
            await setDoc(doc(db, 'users', user.uid), {
              email: user.email,
              displayName: user.displayName,
              firstName: names[0] || '',
              lastName: names.slice(1).join(' ') || '',
              photoURL: user.photoURL,
              role: 'guest',
              isVerified: true,
              isSuspended: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            // Sync AuthContext with the newly written Firestore doc.
            await refreshUser();
          }

          setCurrentStep('travel-style');
        }
      } catch {
        toast.error("We couldn't complete Google sign-in. Please try again.");
      } finally {
        setCheckingRedirect(false);
      }
    };
    checkRedirect();
    // refreshUser is stable (useCallback with no deps); intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = useCallback((step: Step, dir: 'forward' | 'back' = 'forward') => {
    setDirection(dir);
    setCurrentStep(step);
  }, []);

  const toggleArrayItem = (arr: string[], item: string) =>
    arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

  // Handle Google Sign-In (redirect-based)
  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      // Page will redirect to Google - on return, useEffect above picks up the result
    } catch (err: unknown) {
      const authErr = err as { code?: string };
      if (authErr.code === 'auth/popup-closed-by-user' || authErr.code === 'auth/cancelled-popup-request') {
        return; // user cancelled - no toast needed
      }
      toast.error("We couldn't sign you in with Google. Please try again.");
    }
  };

  // Handle final account creation
  const handleCreateAccount = async () => {
    try {
      let userId = googleUid;

      if (!isGoogleUser) {
        // Create account with email/password
        const result = await signUp({
          email,
          password,
          firstName,
          lastName,
          phone: phone || undefined,
        });
        userId = result.user.uid;
      }

      // Save travel preferences if any were selected
      if (userId && (selectedStyles.length || selectedCompanions.length || selectedSeasons.length)) {
        await updateUserProfile(userId, {
          travelPreferences: {
            styles: selectedStyles,
            companions: selectedCompanions,
            seasons: selectedSeasons,
          },
        } as Record<string, unknown>);
      }

      // Sync the AuthContext with the freshly created Firestore doc.
      // Without this, the auth listener may have already fired with the
      // doc still missing, leaving the in-memory profile partial until reload.
      await refreshUser();

      goTo('done');
    } catch (err: unknown) {
      const authErr = err as { code?: string; message?: string };
      if (authErr.code === 'auth/email-already-in-use') {
        toast.error('An account with this email already exists. Try logging in instead.');
      } else if (authErr.code === 'auth/weak-password') {
        toast.error('Password is too weak. Please choose a stronger one.');
      } else if (authErr.code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.');
      } else if (authErr.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error("We couldn't create your account right now. Please try again in a moment.");
      }
    }
  };

  // Show loading while checking for Google redirect result
  if (checkingRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-8 h-8 animate-spin text-primary-500" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Show welcome screen (no progress bar, no wrapper)
  if (currentStep === 'done') {
    return (
      <WelcomeScreen
        firstName={firstName}
        selectedStyles={selectedStyles}
        selectedCompanions={selectedCompanions}
      />
    );
  }

  // Step 1 has its own full layout (hero background)
  if (currentStep === 'welcome') {
    return (
      <StepWelcome
        email={email}
        onEmailChange={setEmail}
        onContinue={() => goTo('account')}
        onGoogleSignIn={handleGoogleSignIn}
        error={null}
      />
    );
  }

  return (
    <div className="relative">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-50">
        <ProgressBar currentStep={STEP_NUMBERS[currentStep]} totalSteps={TOTAL_STEPS} />
      </div>

      {/* Step content with slide animation */}
      <div
        className={`transition-all duration-500 ease-out ${
          direction === 'forward'
            ? 'animate-slide-in-right'
            : 'animate-slide-in-left'
        }`}
        key={currentStep}
      >
        {currentStep === 'account' && (
          <StepAccount
            firstName={firstName}
            lastName={lastName}
            password={password}
            phone={phone}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onPasswordChange={setPassword}
            onPhoneChange={setPhone}
            onContinue={() => goTo('travel-style')}
            onBack={() => goTo('welcome', 'back')}
          />
        )}

        {currentStep === 'travel-style' && (
          <StepTravelStyle
            selectedStyles={selectedStyles}
            onToggleStyle={(s) => setSelectedStyles(toggleArrayItem(selectedStyles, s))}
            onContinue={() => goTo('travelers')}
            onBack={() => goTo(isGoogleUser ? 'welcome' : 'account', 'back')}
            onSkip={() => goTo('travelers')}
          />
        )}

        {currentStep === 'travelers' && (
          <StepTravelers
            selectedCompanions={selectedCompanions}
            selectedSeasons={selectedSeasons}
            onToggleCompanion={(c) => setSelectedCompanions(toggleArrayItem(selectedCompanions, c))}
            onToggleSeason={(s) => setSelectedSeasons(toggleArrayItem(selectedSeasons, s))}
            onContinue={() => goTo('confirm')}
            onBack={() => goTo('travel-style', 'back')}
            onSkip={() => goTo('confirm')}
          />
        )}

        {currentStep === 'confirm' && (
          <StepConfirm
            firstName={firstName}
            lastName={lastName}
            email={email}
            selectedStyles={selectedStyles}
            selectedCompanions={selectedCompanions}
            selectedSeasons={selectedSeasons}
            onSubmit={handleCreateAccount}
            onBack={() => goTo('travelers', 'back')}
            error={null}
          />
        )}
      </div>

      {/* Slide animations */}
      <style>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.4s ease-out;
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OnboardingPage;
